import {
  alternativeReasonCodes,
  alternativesDemandSlugs,
  alternativesQualityContract,
  minimumAlternativesBySource,
} from "~/config/seo-alternatives"

export type AlternativesQualityCandidate = {
  alternativeSlugs: readonly string[]
  demandEvidence: string | null
  featureContrast: string | null
  lastVerifiedAt: Date | string | null
  licenseContrast: string | null
  pricingContrast: string | null
  reasonCodes: readonly string[]
  sourceNotes: string | null
  sourceSlug: string
  status?: "Draft" | "Published"
  summary: string | null
}

export type AlternativesQualityIssue =
  | "source_not_allowlisted"
  | "too_few_alternatives"
  | "duplicate_alternatives"
  | "self_as_alternative"
  | "missing_reason_codes"
  | "missing_pricing_contrast"
  | "missing_license_contrast"
  | "missing_feature_contrast"
  | "missing_source_notes"
  | "missing_demand_evidence"
  | "missing_last_verified_at"
  | "invalid_last_verified_at"
  | "invalid_reason_code"
  | "missing_summary"
  | "summary_too_long"
  | "draft_page"

const demandSlugs = new Set<string>(alternativesDemandSlugs)
const reasonCodes = new Set<string>(alternativeReasonCodes)
const contrastIssueByField = {
  pricingContrast: "missing_pricing_contrast",
  licenseContrast: "missing_license_contrast",
  featureContrast: "missing_feature_contrast",
} as const satisfies Record<
  (typeof alternativesQualityContract.requiredContrastFields)[number],
  AlternativesQualityIssue
>

export function getAlternativesPath(slug: string): string {
  return `/tools/${slug}/alternatives`
}

export function getAlternativesUrl(siteUrl: string, slug: string): string {
  return new URL(getAlternativesPath(slug), siteUrl).toString()
}

export function getAlternativesQualityIssues(
  candidate: AlternativesQualityCandidate,
): AlternativesQualityIssue[] {
  const issues: AlternativesQualityIssue[] = []

  if (candidate.status === "Draft") {
    issues.push("draft_page")
  }

  if (!demandSlugs.has(candidate.sourceSlug)) {
    issues.push("source_not_allowlisted")
  }

  if (
    candidate.alternativeSlugs.length <
    (minimumAlternativesBySource[candidate.sourceSlug] ??
      alternativesQualityContract.minAlternativeCount)
  ) {
    issues.push("too_few_alternatives")
  }

  if (new Set(candidate.alternativeSlugs).size !== candidate.alternativeSlugs.length) {
    issues.push("duplicate_alternatives")
  }

  if (candidate.alternativeSlugs.includes(candidate.sourceSlug)) {
    issues.push("self_as_alternative")
  }

  if (candidate.reasonCodes.length < alternativesQualityContract.minReasonCodeCount) {
    issues.push("missing_reason_codes")
  }

  if (candidate.reasonCodes.some(reasonCode => !reasonCodes.has(reasonCode))) {
    issues.push("invalid_reason_code")
  }

  for (const field of alternativesQualityContract.requiredContrastFields) {
    if (!hasContent(candidate[field])) {
      issues.push(contrastIssueByField[field])
    }
  }

  if (!hasContent(candidate.sourceNotes)) {
    issues.push("missing_source_notes")
  }

  if (!hasContent(candidate.demandEvidence)) {
    issues.push("missing_demand_evidence")
  }

  pushIssue(issues, getLastVerifiedAtIssue(candidate.lastVerifiedAt))

  pushIssue(issues, getSummaryIssue(candidate.summary))

  return issues
}

export function canIndexAlternativesPage(candidate: AlternativesQualityCandidate): boolean {
  return getAlternativesQualityIssues(candidate).length === 0
}

type AlternativesQualityRecord = {
  alternatives: readonly { tool: { slug: string } }[]
  demandEvidence: string | null
  featureContrast: string | null
  lastVerifiedAt: Date | string | null
  licenseContrast: string | null
  pricingContrast: string | null
  reasonCodes: readonly string[]
  sourceNotes: string | null
  sourceTool: { slug: string }
  status?: "Draft" | "Published"
  summary: string | null
}

export function toAlternativesQualityCandidate(
  input: AlternativesQualityRecord,
): AlternativesQualityCandidate {
  return {
    alternativeSlugs: input.alternatives.map(alternative => alternative.tool.slug),
    demandEvidence: input.demandEvidence,
    featureContrast: input.featureContrast,
    lastVerifiedAt: input.lastVerifiedAt,
    licenseContrast: input.licenseContrast,
    pricingContrast: input.pricingContrast,
    reasonCodes: input.reasonCodes,
    sourceNotes: input.sourceNotes,
    sourceSlug: input.sourceTool.slug,
    status: input.status,
    summary: input.summary,
  }
}

function hasContent(value: string | null): boolean {
  return Boolean(value?.trim())
}

function pushIssue(
  issues: AlternativesQualityIssue[],
  issue: AlternativesQualityIssue | null,
): void {
  if (issue) {
    issues.push(issue)
  }
}

function getLastVerifiedAtIssue(value: Date | string | null): AlternativesQualityIssue | null {
  if (!value) {
    return "missing_last_verified_at"
  }

  return isValidDate(value) ? null : "invalid_last_verified_at"
}

function getSummaryIssue(value: string | null): AlternativesQualityIssue | null {
  const summary = value?.trim() ?? ""

  if (!summary) {
    return "missing_summary"
  }

  return summary.length > alternativesQualityContract.maxSummaryLength ? "summary_too_long" : null
}

function isValidDate(value: Date | string | null): boolean {
  if (!value) return false

  if (value instanceof Date) {
    return !Number.isNaN(value.getTime())
  }

  // Contract values are ISO YYYY-MM-DD snapshots; reject malformed or
  // non-calendar strings like "2026-02-30" that Date would silently roll over.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false

  const date = new Date(`${value}T00:00:00.000Z`)
  return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(value)
}
